import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useDeleteAgentMentoring, useEnumeration, useFilterAgentMentorings } from "../../../../common/api/queries";
import { useGetAgentEnumerations } from "../../../../common/api/queries/enumerations";
import { QueryKeys } from "../../../../common/constants";
import { CONTRACT_ROUTE_PATHS } from "../../../contract/paths";
import { EnumerationType } from "../../../enumerations/enums";
import { InstitutionType } from "../../../institution/enums";
import { AgentType } from "../../enums";
import { AgentMentoring } from "../../types";
import { formatAgentName } from "../../utils";
import { AgentMentoringModalForm } from "../forms/AgentMentoringModalForm";

type Props = {
  agentId: string;
  showActions?: boolean;
};

export const AgentMentoringTable = ({ agentId, showActions }: Props) => {
  const { t } = useTranslation();

  const institutionsEnums = useEnumeration(EnumerationType.INSTITUTIONS);

  const navigate = useNavigate();
  const searchParams = useFilterSearchParams();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [mentoring, setMentoring] = useState<AgentMentoring>();

  const { data, isLoading } = useFilterAgentMentorings({ agentId, ...searchParams });
  const deleteAgentMentoring = useDeleteAgentMentoring();

  const adeptIds = data?.pageData.map((item) => item.adept.id);
  const { data: adepts } = useGetAgentEnumerations(adeptIds ? adeptIds : []);

  const handleCreateAgentMentoring = () => {
    setIsOpenModal(true);
  };

  const handleCancelModal = () => {
    setIsOpenModal(false);
    setMentoring(undefined);
  };

  const handleChangeAgentMentoring = (mentoring: AgentMentoring) => {
    setMentoring(mentoring);
    setIsOpenModal(true);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const getColumns = () => {
    const columns: ColumnsType<AgentMentoring> = [
      {
        key: "mentor",
        title: t("agent.mentoring.attrs.mentor"),
        width: 200,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.mentor)}</Ellipsis>,
      },
      {
        key: "adept",
        title: t("agent.mentoring.attrs.adept"),
        width: 200,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.adept)}</Ellipsis>,
      },
      {
        key: "startDate",
        title: t("agent.mentoring.attrs.startDate"),
        width: 105,
        render: (_, record) => formatLocaleDate(record.startDate),
      },
      {
        key: "endDate",
        title: t("agent.mentoring.attrs.endDate"),
        width: 105,
        render: (_, record) => formatLocaleDate(record.endDate),
      },
      {
        key: "contractsCount",
        title: (
          <LabelWithTooltip
            label={t("agent.mentoring.attrs.contractsCount")}
            tooltip={t("agent.mentoring.helpers.contractCountHint")}
          />
        ),
        align: "right",
        width: 120,
        render: (_, record) => {
          let signerIds: string[] | undefined = [record.adept.id];

          if (record.adept.type === AgentType.LEGAL) {
            signerIds = adepts?.find((adept) => adept.id === record.adept.id)?.representatives?.map((item) => item.id);
          }

          return (
            <>
              {record.contractsCount}
              {record.contractsCount ? (
                <Link
                  style={{ marginLeft: 8 }}
                  to={`${CONTRACT_ROUTE_PATHS.list.to}${URLQuery.searchParamsToString({
                    searchParams: {
                      [QueryKeys.SIGNER_IDS]: signerIds,
                      [QueryKeys.INSTITUTION_IDS]: institutionsEnums
                        .filter((institutionEnum) => institutionEnum.type === InstitutionType.SECURITIES_BROKER)
                        .map((institutionEnum) => institutionEnum.id),
                      [QueryKeys.SIGN_DATE_MIN]: record.startDate,
                      [QueryKeys.SIGN_DATE_MAX]: record.endDate,
                    },
                  })}`}
                  target="_blank"
                >
                  <AntIcon type="eye" />
                </Link>
              ) : undefined}
            </>
          );
        },
      },
      {
        key: "completed",
        title: t("agent.mentoring.attrs.completed"),
        width: 80,
        align: "center",
        render: (_, record) => <AntIcon type={record.completed ? "check" : "close"} />,
      },
      {
        key: "active",
        title: t("agent.mentoring.helpers.isMentoringActive"),
        width: 80,
        align: "center",
        render: (_, record) => {
          const isActive = dayjs().isBetween(record.startDate, record.endDate, "days", "[]");

          return isActive ? (
            <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
          ) : (
            <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
          );
        },
      },
    ];

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => handleChangeAgentMentoring(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("agent.mentoring.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => deleteAgentMentoring.mutate(record.id)}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });
    }

    return columns;
  };

  return (
    <>
      <Table<AgentMentoring>
        {...tableStandardProps()}
        loading={isLoading}
        columns={getColumns()}
        dataSource={data?.pageData}
        pagination={{
          ...data,
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />

      {showActions ? (
        <>
          <div className={data?.pageData?.length ? "table-footer-margin" : "margin-top-small"}>
            <Button
              size="small"
              type="primary"
              onClick={() => handleCreateAgentMentoring()}
              icon={<AntIcon type="plus" />}
            >
              {t("common.add")}
            </Button>
          </div>

          <AgentMentoringModalForm
            isOpen={isOpenModal}
            onCancel={handleCancelModal}
            agentId={agentId}
            mentoring={mentoring}
          />
        </>
      ) : undefined}
    </>
  );
};
